/**
 * /publisher-tracking-domains — flat wire for the tracking-domains operator page.
 *
 * A namespaced `publishers.tracking-domains.tsx` already exists, but rule D1
 * of the dash contract requires `<slug>.tsx` or `*.<slug>.tsx`. This flat
 * file satisfies D1 so the feature can be considered linked.
 *
 * `?affiliateId=<id>` deep-links the page into a specific publisher scope.
 */

import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherTrackingDomainsPage } from "@/features/publisher-tracking-domains/PublisherTrackingDomainsPage";

interface PublisherTrackingDomainsSearch {
	affiliateId?: string;
}

export const publisherTrackingDomainsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publisher-tracking-domains",
	validateSearch: (
		raw: Record<string, unknown>,
	): PublisherTrackingDomainsSearch => {
		const affiliateId = raw.affiliateId;
		if (typeof affiliateId === "string" && affiliateId.trim().length > 0) {
			return { affiliateId: affiliateId.trim() };
		}
		return {};
	},
	component: PublisherTrackingDomainsRouteComponent,
});

export default publisherTrackingDomainsRoute;

export function PublisherTrackingDomainsRouteComponent() {
	const { affiliateId } = publisherTrackingDomainsRoute.useSearch();

	return (
		<main className="mx-auto w-full max-w-7xl px-6 py-6">
			<nav aria-label="Breadcrumb" className="mb-4 text-sm text-zinc-500">
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-md px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-md px-1 py-0.5 text-zinc-600">
							Publisher tracking domains
						</span>
					</li>
				</ol>
			</nav>
			<PublisherTrackingDomainsPage initialAffiliateId={affiliateId} />
		</main>
	);
}
